import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import MehrsunPortalApi from "../../../../../../infrastructure/api/mehrsunPortalApi";
import { parseApiErrorMessage } from "../../../../../components/request/case-tabs/mehrsanOperationsUtils";

const pick = (obj, camel, pascal) => obj?.[camel] ?? obj?.[pascal] ?? null;

const normalizeOptions = (res) => {
  const data = res?.data ?? res?.Data;
  return Array.isArray(data) ? data : [];
};

/**
 * انتخاب شرکت توزیع مهرسان + نمایش کپچا و ارسال ثبت‌نام متقاضی.
 */
const MehrsunSendRegistrationCard = ({
  requestInfoId,
  operationsSource,
  canEdit = false,
  disabledReason = "",
  onSuccess,
  /** با افزایش این مقدار، به کارت اسکرول می‌شود و اگر شرکت انتخاب شده باشد ارسال شروع می‌شود. */
  autoStartToken = 0,
  highlight = false,
  /** شرکت توزیع از قبل انتخاب‌شده در بررسی اولیه */
  initialDistributionCompanyId = null,
}) => {
  const cardRef = useRef(null);
  const lastAutoTokenRef = useRef(0);
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [selectedKey, setSelectedKey] = useState("");
  const [startBusy, setStartBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [session, setSession] = useState(null);
  const [captcha, setCaptcha] = useState("");
  const [submitBusy, setSubmitBusy] = useState(false);
  const [refreshBusy, setRefreshBusy] = useState(false);
  const [error, setError] = useState("");
  const [successPassword, setSuccessPassword] = useState("");
  const [pulse, setPulse] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualBusy, setManualBusy] = useState(false);
  const [manualNote, setManualNote] = useState("");
  const [manualReference, setManualReference] = useState("");
  const [manualUsername, setManualUsername] = useState("");
  const [manualPassword, setManualPassword] = useState("");

  const loadCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    try {
      const res = await MehrsunPortalApi.getCompanyOptions(operationsSource);
      if (res?.isSuccess === false) {
        throw new Error(
          parseApiErrorMessage(res, "دریافت فهرست شرکت‌های مهرسان ناموفق بود.")
        );
      }
      const list = normalizeOptions(res);
      setCompanies(list);
      if (initialDistributionCompanyId) {
        const match = list.find((c) => {
          const id = pick(c, "distributionCompanyId", "DistributionCompanyId");
          return Number(id) === Number(initialDistributionCompanyId);
        });
        if (match) {
          const id = pick(match, "distributionCompanyId", "DistributionCompanyId");
          setSelectedKey(`id:${id}`);
        }
      }
    } catch (err) {
      toast.error(err?.message || "دریافت فهرست شرکت‌های مهرسان ناموفق بود.");
    } finally {
      setLoadingCompanies(false);
    }
  }, [operationsSource, initialDistributionCompanyId]);

  useEffect(() => {
    if (!canEdit) return;
    loadCompanies();
  }, [canEdit, loadCompanies]);

  const selectedCompany = useMemo(() => {
    if (!selectedKey) return null;
    return (
      companies.find((c) => {
        const id = pick(c, "distributionCompanyId", "DistributionCompanyId");
        const code = pick(c, "mehrsunCode", "MehrsunCode");
        if (selectedKey.startsWith("id:")) {
          return String(id) === selectedKey.slice(3);
        }
        return `code:${code}` === selectedKey;
      }) || null
    );
  }, [companies, selectedKey]);

  const captchaSrc = useMemo(() => {
    const b64 = pick(session, "captchaImageBase64", "CaptchaImageBase64");
    const type =
      pick(session, "captchaContentType", "CaptchaContentType") || "image/png";
    if (!b64) return "";
    return `data:${type};base64,${b64}`;
  }, [session]);

  const preview = pick(session, "preview", "Preview");

  const handleStart = useCallback(async () => {
    if (!requestInfoId) {
      toast.error("شناسه درخواست نامعتبر است.");
      return;
    }
    if (disabledReason) {
      toast.warning(disabledReason);
      return;
    }
    if (!selectedCompany) {
      toast.error("ابتدا شرکت توزیع مهرسان را انتخاب کنید.");
      cardRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      setPulse(true);
      return;
    }

    setStartBusy(true);
    setError("");
    setSuccessPassword("");
    try {
      const id = pick(
        selectedCompany,
        "distributionCompanyId",
        "DistributionCompanyId"
      );
      const code = pick(selectedCompany, "mehrsunCode", "MehrsunCode");
      const res = await MehrsunPortalApi.startRegistration(operationsSource, {
        RequestInfoId: Number(requestInfoId),
        DistributionCompanyId: id ? Number(id) : null,
        MehrsunCode: code,
      });
      if (res?.isSuccess === false) {
        throw new Error(
          parseApiErrorMessage(res, "شروع ثبت‌نام مهرسان ناموفق بود.")
        );
      }
      const data = res?.data ?? res?.Data;
      setSession(data);
      setCaptcha("");
      setDialogOpen(true);
    } catch (err) {
      toast.error(err?.message || "شروع ثبت‌نام مهرسان ناموفق بود.");
    } finally {
      setStartBusy(false);
    }
  }, [disabledReason, operationsSource, requestInfoId, selectedCompany]);

  useEffect(() => {
    if (!autoStartToken || autoStartToken === lastAutoTokenRef.current) return;
    lastAutoTokenRef.current = autoStartToken;
    cardRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    setPulse(true);
    if (selectedCompany && !disabledReason) {
      handleStart();
    } else {
      toast.info("شرکت توزیع را انتخاب کنید، سپس «ارسال به مهرسان» را بزنید.");
    }
  }, [autoStartToken, disabledReason, handleStart, selectedCompany]);

  useEffect(() => {
    if (!pulse) return undefined;
    const t = setTimeout(() => setPulse(false), 2500);
    return () => clearTimeout(t);
  }, [pulse]);

  const applySessionPatch = (data) => {
    if (!data) return;
    setSession((prev) => ({
      ...(prev || {}),
      ...data,
      captchaImageBase64:
        pick(data, "captchaImageBase64", "CaptchaImageBase64") ||
        pick(prev, "captchaImageBase64", "CaptchaImageBase64"),
      captchaContentType:
        pick(data, "captchaContentType", "CaptchaContentType") ||
        pick(prev, "captchaContentType", "CaptchaContentType"),
      sessionId:
        pick(data, "sessionId", "SessionId") ||
        pick(prev, "sessionId", "SessionId"),
    }));
  };

  const handleRefreshCaptcha = async () => {
    const sessionId = pick(session, "sessionId", "SessionId");
    if (!sessionId) return;
    setRefreshBusy(true);
    setError("");
    try {
      const res = await MehrsunPortalApi.refreshCaptcha(operationsSource, {
        SessionId: sessionId,
      });
      if (res?.isSuccess === false) {
        throw new Error(parseApiErrorMessage(res, "دریافت کپچای جدید ناموفق بود."));
      }
      applySessionPatch(res?.data ?? res?.Data);
      setCaptcha("");
    } catch (err) {
      setError(err?.message || "دریافت کپچای جدید ناموفق بود.");
    } finally {
      setRefreshBusy(false);
    }
  };

  const handleSubmit = async () => {
    const sessionId = pick(session, "sessionId", "SessionId");
    if (!sessionId) {
      setError("نشست ثبت‌نام منقضی شده است.");
      return;
    }
    if (!String(captcha || "").trim()) {
      setError("کد کپچا را وارد کنید.");
      return;
    }

    setSubmitBusy(true);
    setError("");
    try {
      const res = await MehrsunPortalApi.submitRegistration(operationsSource, {
        SessionId: sessionId,
        Captcha: String(captcha).trim(),
      });
      const data = res?.data ?? res?.Data;
      if (res?.isSuccess === false) {
        if (data && pick(data, "requiresNewCaptcha", "RequiresNewCaptcha")) {
          applySessionPatch(data);
          setCaptcha("");
        }
        throw new Error(
          parseApiErrorMessage(
            res,
            data?.message || data?.Message || "ثبت‌نام ناموفق بود."
          )
        );
      }

      const password =
        pick(data, "generatedPassword", "GeneratedPassword") || "";
      setSuccessPassword(password);
      toast.success(
        pick(data, "message", "Message") ||
          res?.message ||
          "ثبت‌نام در مهرسان با موفقیت انجام شد."
      );
      setDialogOpen(false);
      setSession(null);
      onSuccess?.(data);
      if (password) {
        toast.info(`نام کاربری و رمز عبور مهرسان: کد ملی (${password})`, {
          autoClose: 12000,
        });
      }
    } catch (err) {
      setError(err?.message || "ثبت‌نام مهرسان ناموفق بود.");
    } finally {
      setSubmitBusy(false);
    }
  };

  const handleOpenManual = () => {
    if (disabledReason) {
      toast.warning(disabledReason);
      return;
    }
    if (!selectedCompany) {
      toast.info("ابتدا شرکت توزیع را انتخاب کنید.");
      return;
    }
    setError("");
    setManualOpen(true);
  };

  const handleManualSave = async () => {
    if (!selectedCompany || !requestInfoId) return;
    setManualBusy(true);
    setError("");
    try {
      const companyId = pick(
        selectedCompany,
        "distributionCompanyId",
        "DistributionCompanyId"
      );
      const code = pick(selectedCompany, "mehrsunCode", "MehrsunCode");
      const res = await MehrsunPortalApi.registerManual(operationsSource, {
        requestInfoId: Number(requestInfoId),
        distributionCompanyId: companyId ? Number(companyId) : null,
        mehrsunCode: code || null,
        referenceCode: manualReference.trim() || null,
        username: manualUsername.trim() || null,
        password: manualPassword.trim() || null,
        note: manualNote.trim() || null,
      });
      if (res?.isSuccess === false) {
        throw new Error(
          parseApiErrorMessage(res, "ثبت دستی فرایند مهرسان ناموفق بود.")
        );
      }
      toast.success(res?.message || "فرایند مهرسان به‌صورت دستی ثبت شد.");
      setManualOpen(false);
      setManualNote("");
      setManualReference("");
      setManualUsername("");
      setManualPassword("");
      onSuccess?.(res?.data ?? res?.Data ?? res);
    } catch (err) {
      const msg = err?.message || "ثبت دستی فرایند مهرسان ناموفق بود.";
      setError(msg);
      toast.error(msg);
    } finally {
      setManualBusy(false);
    }
  };

  if (!canEdit) return null;

  const emphasized = highlight || pulse;

  return (
    <>
      <Paper
        ref={cardRef}
        id="mehrsun-send-registration-card"
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 2,
          borderWidth: emphasized ? 2 : 1,
          borderColor: emphasized ? "warning.main" : "divider",
          bgcolor: emphasized ? "warning.50" : "background.paper",
          transition: "border-color 0.2s, background-color 0.2s",
        }}
      >
        <Stack spacing={1.5}>
          <Typography variant="subtitle2" fontWeight={700}>
            ارسال به مهرسان (ثبت‌نام اولیه)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ارسال اتوماتیک با کپچا، یا اگر قبلاً در مهرسان ثبت شده، «ثبت دستی»
            را بزنید تا فرایند در سولارنت به‌روز شود.
          </Typography>
          {disabledReason ? (
            <Alert severity="warning" variant="outlined">
              {disabledReason}
            </Alert>
          ) : null}
          {successPassword ? (
            <Alert severity="success" variant="outlined">
              نام کاربری و رمز عبور مهرسان برابر کد ملی است:{" "}
              <Box component="span" fontWeight={700}>
                {successPassword}
              </Box>
            </Alert>
          ) : null}
          {error && !dialogOpen && !manualOpen ? (
            <Alert severity="error" variant="outlined">
              {error}
            </Alert>
          ) : null}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ sm: "center" }}
          >
            <FormControl
              fullWidth
              size="small"
              disabled={loadingCompanies || startBusy || manualBusy}
              required
            >
              <InputLabel id="mehrsun-company-label">شرکت توزیع مهرسان</InputLabel>
              <Select
                labelId="mehrsun-company-label"
                label="شرکت توزیع مهرسان"
                value={selectedKey}
                displayEmpty
                onChange={(e) => setSelectedKey(e.target.value)}
              >
                <MenuItem value="">
                  <em>انتخاب کنید…</em>
                </MenuItem>
                {companies.map((c) => {
                  const id = pick(
                    c,
                    "distributionCompanyId",
                    "DistributionCompanyId"
                  );
                  const code = pick(c, "mehrsunCode", "MehrsunCode");
                  const name = pick(c, "name", "Name");
                  const key = id ? `id:${id}` : `code:${code}`;
                  return (
                    <MenuItem key={key} value={key}>
                      {name} ({code})
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <Button
              type="button"
              variant="contained"
              color="warning"
              startIcon={
                startBusy ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
              disabled={
                Boolean(disabledReason) ||
                startBusy ||
                manualBusy ||
                loadingCompanies ||
                !selectedCompany
              }
              onClick={handleStart}
              sx={{ whiteSpace: "nowrap", minWidth: 160 }}
            >
              ارسال به مهرسان
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="primary"
              startIcon={<EditOutlinedIcon />}
              disabled={
                Boolean(disabledReason) ||
                startBusy ||
                manualBusy ||
                loadingCompanies ||
                !selectedCompany
              }
              onClick={handleOpenManual}
              sx={{ whiteSpace: "nowrap", minWidth: 140 }}
            >
              ثبت دستی
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={submitBusy ? undefined : () => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>تأیید ثبت‌نام در مهرسان — وارد کردن کپچا</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              اطلاعات متقاضی از پرونده پر شده است. کپچای مهرسان را وارد کنید تا
              ثبت‌نام در شرکت{" "}
              <Box component="span" fontWeight={700}>
                {pick(
                  session,
                  "distributionCompanyName",
                  "DistributionCompanyName"
                ) || "انتخاب‌شده"}
              </Box>{" "}
              انجام شود.
            </Typography>

            {preview ? (
              <Box
                sx={{
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  p: 1.5,
                  fontSize: 13,
                }}
              >
                <div>
                  {pick(preview, "firstName", "FirstName")}{" "}
                  {pick(preview, "lastName", "LastName")}
                </div>
                <div>
                  کد ملی: {pick(preview, "nationalCode", "NationalCode")}
                </div>
                <div>موبایل: {pick(preview, "mobile", "Mobile")}</div>
              </Box>
            ) : null}

            {error ? <Alert severity="error">{error}</Alert> : null}

            <Stack direction="row" spacing={2} alignItems="center">
              {captchaSrc ? (
                <Box
                  component="img"
                  src={captchaSrc}
                  alt="کپچا مهرسان"
                  sx={{
                    height: 48,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "#fff",
                  }}
                />
              ) : (
                <CircularProgress size={24} />
              )}
              <Button
                type="button"
                size="small"
                startIcon={
                  refreshBusy ? <CircularProgress size={14} /> : <RefreshIcon />
                }
                disabled={refreshBusy || submitBusy}
                onClick={handleRefreshCaptcha}
              >
                کپچای جدید
              </Button>
            </Stack>

            <TextField
              label="کد کپچا"
              value={captcha}
              onChange={(e) => setCaptcha(e.target.value)}
              inputProps={{ maxLength: 4, dir: "ltr" }}
              autoFocus
              fullWidth
              size="small"
              disabled={submitBusy}
              helperText="عدد کپچا را از تصویر بخوانید و وارد کنید"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            onClick={() => setDialogOpen(false)}
            disabled={submitBusy}
          >
            انصراف
          </Button>
          <Button
            type="button"
            variant="contained"
            color="warning"
            onClick={handleSubmit}
            disabled={submitBusy}
            startIcon={
              submitBusy ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            تأیید و ثبت‌نام در مهرسان
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={manualOpen}
        onClose={manualBusy ? undefined : () => setManualOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>ثبت / به‌روزرسانی دستی فرایند مهرسان</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info" variant="outlined">
              برای پرونده‌هایی که قبلاً در سامانه مهرسان ثبت شده‌اند؛ بدون ارسال
              اتوماتیک، مرحله ثبت‌نام در سولارنت تکمیل می‌شود.
            </Alert>
            <Typography variant="body2">
              شرکت انتخاب‌شده:{" "}
              <Box component="span" fontWeight={700}>
                {pick(selectedCompany, "name", "Name") || "—"} (
                {pick(selectedCompany, "mehrsunCode", "MehrsunCode") || "—"})
              </Box>
            </Typography>
            {error ? (
              <Alert severity="error" variant="outlined">
                {error}
              </Alert>
            ) : null}
            <TextField
              label="کد / مرجع (اختیاری)"
              value={manualReference}
              onChange={(e) => setManualReference(e.target.value)}
              fullWidth
              size="small"
              disabled={manualBusy}
            />
            <TextField
              label="نام کاربری مهرسان (اختیاری)"
              value={manualUsername}
              onChange={(e) => setManualUsername(e.target.value)}
              fullWidth
              size="small"
              disabled={manualBusy}
              inputProps={{ dir: "ltr" }}
            />
            <TextField
              label="رمز عبور مهرسان (اختیاری)"
              value={manualPassword}
              onChange={(e) => setManualPassword(e.target.value)}
              fullWidth
              size="small"
              disabled={manualBusy}
              inputProps={{ dir: "ltr" }}
            />
            <TextField
              label="توضیحات"
              value={manualNote}
              onChange={(e) => setManualNote(e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={2}
              disabled={manualBusy}
              placeholder="مثلاً: ثبت دستی در پورتال مهرسان در تاریخ …"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            onClick={() => setManualOpen(false)}
            disabled={manualBusy}
          >
            انصراف
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={handleManualSave}
            disabled={manualBusy}
            startIcon={
              manualBusy ? <CircularProgress size={16} color="inherit" /> : (
                <EditOutlinedIcon />
              )
            }
          >
            ذخیره ثبت دستی
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

MehrsunSendRegistrationCard.propTypes = {
  requestInfoId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  operationsSource: PropTypes.string.isRequired,
  canEdit: PropTypes.bool,
  disabledReason: PropTypes.string,
  onSuccess: PropTypes.func,
  autoStartToken: PropTypes.number,
  highlight: PropTypes.bool,
  initialDistributionCompanyId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
};

export default MehrsunSendRegistrationCard;
